import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import APIUtil from 'util/APIUtil';

import {
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  TaskSchemaNodeSectionType,
} from './processFlowchartConfigEditorConstants';

export const NODE_IDENTIFIER_PREFIX = 'node';
export const FIELD_IDENTIFIER_PREFIX = 'field';

export const FIELD_TYPE_INPUT = 'input' as const;
export const FIELD_TYPE_OUTPUT = 'output' as const;

export const HANDLE_TYPE_NODE = 'node' as const;
export const HANDLE_TYPE_FIELD = 'field' as const;

export const HANDLE_DIRECTION_SOURCE = 'source' as const;
export const HANDLE_DIRECTION_TARGET = 'target' as const;

// Type definitions
export type FieldType = typeof FIELD_TYPE_INPUT | typeof FIELD_TYPE_OUTPUT;
export type HandleCategory = typeof HANDLE_TYPE_NODE | typeof HANDLE_TYPE_FIELD;
export type HandleDirection = typeof HANDLE_DIRECTION_SOURCE | typeof HANDLE_DIRECTION_TARGET;

export function generateTaskSchemaNodeIdentifier(taskSchema: {id: string}): string {
  const {id} = taskSchema;
  // Task schemas can be repeated in a flowchart, add a unique identifier to distinguish nodes
  const hash = uuidv4();
  return `${NODE_IDENTIFIER_PREFIX}:${id}:${hash}`;
}

export function generateLogicNodeIdentifier(nodeType: ProcessFlowchartNodeType): string {
  const hash = uuidv4();
  return `${NODE_IDENTIFIER_PREFIX}:${nodeType}:${hash}`;
}

export interface MakeDataFlowEdgeHandleParams {
  nodeId: string;
  fieldClientId: string;
  sectionType: TaskSchemaNodeSectionType;
  handleType: 'source' | 'target';
}

export function makeDataFlowEdgeHandle({
  nodeId,
  fieldClientId,
  sectionType,
  handleType,
}: MakeDataFlowEdgeHandleParams): string {
  return `${FIELD_IDENTIFIER_PREFIX}|${nodeId}|${fieldClientId}|${sectionType}|${handleType}`;
}

/** This function signals a hard dependency on schema fields. It should only be used in places that do not yet
 * support task definitions.
 *
 * @deprecated Instead, refactor to use field definitions.
 */
export function getSchemaFieldIdFromRowIdentifier(rowIdentifier: string): string {
  assert(APIUtil.isSchemaField(rowIdentifier), `Invalid fieldId: ${rowIdentifier}`);
  return rowIdentifier;
}

/**
 * Get the node ID, field client ID, and section type from a data flow edge handle.
 * The handle has format field|[nodeId]|[fieldClientId]|[sectionType]|[handleType], e.g. field|node123|Field1|INPUTS|source
 */
export function getFieldInfoFromDataFlowEdgeHandle(handle: string): {
  nodeId: string;
  fieldClientId: string;
  sectionType: TaskSchemaNodeSectionType;
} {
  const identifierParts = handle.split('|');
  assert(
    identifierParts.length === 5,
    `Invalid data flow edge handle: ${handle}, identifierParts: ${identifierParts}`
  );
  const [_, nodeId, fieldClientId, sectionType, __] = identifierParts;
  if (!isTaskSchemaNodeSectionType(sectionType)) {
    throw new Error(`Invalid sectionType: ${sectionType}`);
  } else {
    return {nodeId, fieldClientId, sectionType};
  }
}

export function doesDataFlowEdgeHandleReferenceRow(dataFlowEdgeHandle: string, rowClientId: string): boolean {
  return rowClientId === getFieldInfoFromDataFlowEdgeHandle(dataFlowEdgeHandle).fieldClientId;
}

export function getEdgeTypeFromHandleId(handleId: string): ProcessFlowchartEdgeType {
  if (handleId.startsWith(FIELD_IDENTIFIER_PREFIX)) {
    return ProcessFlowchartEdgeType.DATA_FLOW;
  }
  return ProcessFlowchartEdgeType.CONTROL_FLOW;
}

export function getDirectionFromHandleId(handleId: string): HandleDirection {
  return handleId.endsWith('-source') || handleId.endsWith('|source')
    ? HANDLE_DIRECTION_SOURCE
    : HANDLE_DIRECTION_TARGET;
}

export interface HandleInfo {
  handleCategory: HandleCategory;
  direction: HandleDirection;
  nodeId: string;
  fieldType?: FieldType; // only for field handles
  fieldClientId?: string; // only for field handles
  sectionType?: TaskSchemaNodeSectionType; // only for field handles
}

/**
 * Parse a handle ID to extract information about the handle type and properties
 */
export function parseHandleId(handleId: string): HandleInfo {
  const edgeType = getEdgeTypeFromHandleId(handleId);
  const direction = getDirectionFromHandleId(handleId);

  if (edgeType === ProcessFlowchartEdgeType.DATA_FLOW) {
    const {nodeId, fieldClientId, sectionType} = getFieldInfoFromDataFlowEdgeHandle(handleId);

    const fieldType = sectionType === TaskSchemaNodeSectionType.INPUTS ? FIELD_TYPE_INPUT : FIELD_TYPE_OUTPUT;

    return {
      handleCategory: HANDLE_TYPE_FIELD,
      direction,
      fieldType,
      nodeId,
      fieldClientId,
      sectionType,
    };
  } else {
    const nodeId = handleId.replace(/-(?:source|target)$/, '');

    return {
      handleCategory: HANDLE_TYPE_NODE,
      direction,
      nodeId,
    };
  }
}

export function getFieldTypeFromSectionName(sectionName: string): FieldType {
  return sectionName === TaskSchemaNodeSectionType.INPUTS ? FIELD_TYPE_INPUT : FIELD_TYPE_OUTPUT;
}

/**
 * Typeguard that checks if a string is a valid TaskSchemaNodeSectionType
 * @param value - The string to check
 * @returns true if the string is a valid TaskSchemaNodeSectionType, false otherwise
 */
export function isTaskSchemaNodeSectionType(value: string): value is TaskSchemaNodeSectionType {
  return Object.values(TaskSchemaNodeSectionType).includes(value as TaskSchemaNodeSectionType);
}
